import React from 'react';

import { MoreHorizontalIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import { ActionsMenu } from 'components/ActionsMenu/ActionsMenu';
import StatusPill from 'components/Pills/StatusPills';

/**
 * MyComponent Props
 *
 * @param {Object} category - Object representing the category
 * @param {string} category.icon - Icon for the category (emoji or component)
 * @param {string} category.text - Text label for the category
 *
 * @param {string|null} title - Main title of the component
 * @param {string|null} description - Description text
 *
 * @param {Array<Object>} tags - Array of tag objects
 * @param {string} tags[].label - Label text for the tag
 * @param {string} tags[].type - Type of tag (e.g., 'success', 'warning', 'info')
 *
 * @param {string|null} subtext - Optional subtext
 *
 * @param {Array<Object>} actions - Array of action objects
 * @param {string} actions[].label - Label for the action button
 * @param {function} actions[].onClick - Click handler for the action
 *
 * @param {string|null} className - Optional additional CSS classes
 * @param {boolean} disabled - Disable the component
 * @param {function} onClick - Click handler for the component
 */

export const ItemOverviewCard = ({
  category = {},
  title = null,
  description = null,
  tags = [],
  subtext = null,
  actions = [],
  className = null,
  disabled = false,
  image = null,
  icon = MoreHorizontalIcon,
  onIconClick = () => {},
  onClick = () => {},
}) => (
  <div
    className={cn(
      'bg-white rounded-3xl shadow-sm p-8 w-full min-w-0 border border-neutral-4 flex flex-col gap-4 cursor-pointer hover:shadow-md transition-all duration-300 hover:border-primary-2',
      className,
      disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : '',
    )}
    onClick={onClick}>
    <div className="flex items-center justify-between align-start">
      <div className="flex items-center gap-2">
        <HugeiconsIcon icon={category.icon} className="w-8 h-8 text-neutral-6" />
        <span className="text-lg font-regular text-neutral-6">{category.text}</span>
      </div>
      <div className="flex items-center" onClick={e => e.stopPropagation()}>
        {actions?.length > 0 ? (
          <ActionsMenu
            newIconLibrary
            small
            triggerIcon={MoreHorizontalIcon}
            className="[&_svg]:text-neutral-6 [&_svg]:stroke-[2]">
            {actions.map((action, index) => (
              <ActionsMenu.Item
                key={action.label ?? index}
                title={action.label}
                onClick={action.onClick}
                icon={action.icon}
                newIconLibrary
                type={action.type ?? 'secondary'}
                disabled={action.disabled}
              />
            ))}
          </ActionsMenu>
        ) : (
          <HugeiconsIcon
            icon={icon}
            onClick={onIconClick}
            className="text-neutral-6 cursor-pointer"
            size={20}
            strokeWidth={2}
          />
        )}
      </div>
    </div>
    <div className="flex flex-col">
      {image && <img src={image} alt={title} className="w-full h-48 object-cover rounded-lg" />}
      <span className="text-2xl font-medium text-neutral-7">{title}</span>
      <span
        className={`text-xl font-light line-clamp-2 overflow-hidden [&_p]:m-0 min-w-0  ${description ? 'text-neutral-6' : 'text-text-tertiary'}`}>
        {description ? description : 'No description available'}
      </span>
    </div>
    <div className="flex items-center gap-2">
      {tags.map((tag, i) =>
        React.isValidElement(tag) ? (
          <React.Fragment key={i}>{tag}</React.Fragment>
        ) : (
          <StatusPill key={tag.label} status={tag.type} text={tag.label} className="px-4" />
        ),
      )}
    </div>
    <div className="flex items-center gap-2">
      <span className="text-lg font-light text-color-tertiary">{subtext}</span>
    </div>
  </div>
);

ItemOverviewCard.propTypes = {
  category: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  subtext: PropTypes.string.isRequired,
  actions: PropTypes.arrayOf(PropTypes.node).isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};
